// src/services/notificationService.ts
import { toast as sonner } from "vue-sonner";
import { Modal } from "ant-design-vue";
import type { I18n } from "vue-i18n";

// 用于持有 i18n 实例
let i18n: I18n | null = null;

// 用于简单的通知去重
const activeNotifications = new Set<string>();

/**
 * 初始化服务，注入 i18n 实例
 * @param i18nInstance - The global i18n instance from main.ts
 */
function init(i18nInstance: I18n) {
  i18n = i18nInstance;
}

/**
 * 翻译函数
 * @param key - i18n key
 * @param params - i18n params
 * @returns Translated string
 */
function t(key: string, params?: Record<string, unknown>): string {
  if (!i18n) {
    console.warn("NotificationService not initialized with i18n instance.");
    return key; // Fallback to key if not initialized
  }
  // @ts-expect-error - Using global t function from i18n instance
  return i18n.global.t(key, params);
}

/**
 * 显示一个轻量级成功通知
 * @param messageKey - i18n key for the message
 * @param options - Optional: { params, duration, key }
 */
function success(
  messageKey: string,
  options?: {
    params?: Record<string, unknown>;
    duration?: number;
    key?: string;
  },
) {
  if (options?.key && activeNotifications.has(options.key)) return;

  sonner.success(t(messageKey, options?.params), {
    duration: options?.duration,
    onAutoClose: () => options?.key && activeNotifications.delete(options.key),
    onDismiss: () => options?.key && activeNotifications.delete(options.key),
  });

  if (options?.key) activeNotifications.add(options.key);
}

/**
 * 显示一个轻量级错误通知
 * @param messageKey - i18n key for the message
 * @param options - Optional: { params, duration, key }
 */
function error(
  messageKey: string,
  options?: {
    params?: Record<string, unknown>;
    duration?: number;
    key?: string;
  },
) {
  if (options?.key && activeNotifications.has(options.key)) return;

  sonner.error(t(messageKey, options?.params), {
    duration: options?.duration ?? 5000, // 错误消息默认显示更久
    onAutoClose: () => options?.key && activeNotifications.delete(options.key),
    onDismiss: () => options?.key && activeNotifications.delete(options.key),
  });

  if (options?.key) activeNotifications.add(options.key);
}

/**
 * 显示一个轻量级信息通知
 * @param messageKey - i18n key for the message
 * @param options - Optional: { params, duration, key }
 */
function info(
  messageKey: string,
  options?: {
    params?: Record<string, unknown>;
    duration?: number;
    key?: string;
  },
) {
  if (options?.key && activeNotifications.has(options.key)) return;

  sonner.info(t(messageKey, options?.params), {
    duration: options?.duration,
    onAutoClose: () => options?.key && activeNotifications.delete(options.key),
    onDismiss: () => options?.key && activeNotifications.delete(options.key),
  });

  if (options?.key) activeNotifications.add(options.key);
}

/**
 * 显示一个轻量级警告通知
 * @param messageKey - i18n key for the message
 * @param options - Optional: { params, duration, key }
 */
function warning(
  messageKey: string,
  options?: {
    params?: Record<string, unknown>;
    duration?: number;
    key?: string;
  },
) {
  if (options?.key && activeNotifications.has(options.key)) return;

  sonner.warning(t(messageKey, options?.params), {
    duration: options?.duration ?? 4000, // 警告消息适中显示时长
    onAutoClose: () => options?.key && activeNotifications.delete(options.key),
    onDismiss: () => options?.key && activeNotifications.delete(options.key),
  });

  if (options?.key) activeNotifications.add(options.key);
}

/**
 * 显示一个确认对话框 (Modal)
 * @param titleKey - i18n key for the title
 * @param contentKey - i18n key for the content
 * @param options - Optional: { params for title/content }
 * @returns Promise<boolean> - Resolves true if confirmed, false otherwise
 */
function confirm(
  titleKey: string,
  contentKey: string,
  options?: {
    titleParams?: Record<string, any>;
    contentParams?: Record<string, any>;
  },
): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: t(titleKey, options?.titleParams),
      content: t(contentKey, options?.contentParams),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/**
 * 清除所有活跃的通知去重标记
 * 用于清理或重置通知状态
 */
function clearActiveNotifications() {
  activeNotifications.clear();
}

/**
 * 手动关闭所有 toast 通知
 */
function dismissAll() {
  sonner.dismiss();
  clearActiveNotifications();
}

export const notificationService = {
  init,
  success,
  error,
  info,
  warning,
  confirm,
  clearActiveNotifications,
  dismissAll,
};

// 导出类型定义供 TypeScript 使用
export type NotificationOptions = {
  params?: Record<string, any>;
  duration?: number;
  key?: string;
};

export type ConfirmOptions = {
  titleParams?: Record<string, any>;
  contentParams?: Record<string, any>;
};
